<template>
  <div
    class="test-case-item p-4 mt-4 relative border rounded-sm"
    tabindex="0"
    @keyup.delete="handleDelete"
  >
    <details ref="testCaseTitle" @toggle="toggled = !toggled">
      <summary class="truncate w-1/2 mb-2 cursor-default">{{
        testCase.title
      }}</summary>
      <div class="flex">
        <div class="w-1/2">
          <MarkdownReadingEditor
            v-if="toggled"
            :value="testCase.title"
            @input="handleTitleInput"
          />
        </div>
        <div style="max-height: 380px;" class="overflow-auto w-1/2">
          <div class="markdown-body px-4">
            <VueMarkdown
              v-if="testCase.title"
              :source="getRenderedMarkdownToHTML(testCase.title)"
            />
          </div>
        </div>
      </div>
    </details>
    <div class="flex py-2">
      <div class="flex items-center pr-4">
        <label :for="`${index}-required`" class="py-2">Required</label>
        <input
          :id="`${index}-required`"
          type="checkbox"
          class="ml-2"
          :checked="testCase.is_required"
          @input="updateUserInputs('is_required', $event.target.checked)"
        />
      </div>
      <div class="flex items-center">
        <label :for="`${index}-points`">Points</label>
        <input
          :id="`${index}-points`"
          type="number"
          class="ml-2"
          :value="testCase.points"
          @input="updateUserInputs('points', Number($event.target.value))"
        />
      </div>
      <div class="flex items-cente ml-2">
        <label :for="`${index}-eval`">Evalution Type</label>
        <select
          :id="`${index}-eval`"
          class="ml-2 bg-white border border-grey rounded-sm"
          @change="updateUserInputs('eval', $event.target.value)"
        >
          <option :selected="testCase.eval === 'automatic'" value="automatic"
            >Automatic</option
          >
          <option :selected="testCase.eval === 'manual'" value="manual"
            >Manual</option
          >
        </select>
      </div>
    </div>
    <button
      class="remove-icon ml-4 cursor-pointer"
      @click="$emit('remove-test-case', { sectionIndex, index })"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class=""
        width="14"
        height="14"
        viewBox="0 0 24 24"
      >
        <path
          d="M18.5 15c-2.484 0-4.5 2.015-4.5 4.5s2.016 4.5 4.5 4.5c2.482 0 4.5-2.015 4.5-4.5s-2.018-4.5-4.5-4.5zm2.5 5h-5v-1h5v1zm-5-11v4.501c-.748.313-1.424.765-2 1.319v-5.82c0-.552.447-1 1-1s1 .448 1 1zm-4 0v10c0 .552-.447 1-1 1s-1-.448-1-1v-10c0-.552.447-1 1-1s1 .448 1 1zm1.82 15h-11.82v-18h2v16h8.502c.312.749.765 1.424 1.318 2zm-6.82-16c.553 0 1 .448 1 1v10c0 .552-.447 1-1 1s-1-.448-1-1v-10c0-.552.447-1 1-1zm14-4h-20v-2h5.711c.9 0 1.631-1.099 1.631-2h5.316c0 .901.73 2 1.631 2h5.711v2zm-1 2v7.182c-.482-.115-.983-.182-1.5-.182l-.5.025v-7.025h2z"
        />
      </svg>
    </button>
    <small class="remove-text">Hit Delete to remove</small>
  </div>
</template>

<script>
import VueMarkdown from "vue-markdown"
import MarkdownReadingEditor from "@/components/questions/MarkdownReadingEditor"
import markdownHelpers from "@/utils/markdownEditorHelpers"

export default {
  components: { MarkdownReadingEditor, VueMarkdown },
  props: {
    testCase: {
      type: Object,
      required: true,
      default() {
        return {}
      },
    },
    index: {
      type: Number,
      required: true,
    },
    sectionIndex: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      toggled: false,
      markdownRenderer: undefined,
    }
  },
  created() {
    this.markdownRenderer = window.markdownit()
  },
  mounted() {
    /* open the details tag if it's a new test case */
    if (!this.testCase.title && !this.testCase.id) {
      this.$refs.testCaseTitle.open = true
    }
  },
  methods: {
    handleTitleInput(content) {
      this.updateUserInputs("title", content)
    },
    handleDelete(e) {
      if (e.target.classList.contains("test-case-item")) {
        this.$emit("remove-test-case", this.index)
      }
    },
    getRenderedMarkdownToHTML(content) {
      return this.markdownRenderer.render(
        markdownHelpers.decodeIDsToMarkdownLinks(content)
      )
    },
    updateUserInputs(key, value) {
      const payload = {
        sectionIndex: this.sectionIndex,
        index: this.index,
        key,
        value,
      }
      this.$emit("update-user-input", payload)
    },
  },
}
</script>

<style scoped>
.test-case-item {
  color: #5a5a5a;
}

.test-case-item .remove-text {
  display: none;
  position: absolute;
  right: 3rem;
  bottom: 0.7rem;
  color: red;
}

.remove-icon {
  visibility: hidden;
  position: absolute;
  right: 1rem;
  bottom: 1rem;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  border-radius: 0;
}

.remove-icon > svg {
  width: 1.5rem;
  height: 1.5rem;
  fill: red;
}

.test-case-item:focus-within {
  background: #ececec;
}

.test-case-item:focus .remove-text,
.test-case-item:focus .remove-icon,
.test-case-item:hover .remove-icon {
  display: block;
  visibility: visible;
}

.test-case-item:focus {
  outline: 2px solid #74caff;
}

.test-case-item input,
.test-case-item select,
.test-case-item label {
  color: #5a5a5a;
}

.test-case-item input:active,
.test-case-item input:focus,
.test-case-item button:active,
.test-case-item button:focus,
.test-case-item select:active,
.test-case-item select:focus {
  border: 1px solid #c7c7c7;
  outline: 2px solid #74caff;
}

.remove-icon:hover,
.remove-icon:focus {
  background: transparent;
}

.remove-icon:hover > svg {
  fill: #c20000;
}
</style>
